import React, { useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'
import echarts from 'echarts/lib/echarts'
import i18n from 'simple-react-i18n'
import { range, orderBy, round, keys, groupBy, entries, compact } from 'lodash'
import { shallowEqual, useSelector } from 'react-redux'
import { exportPictureIcon, exportExcelIcon, fullScreenIcon, fullScreenExitIcon, operationIcon } from 'components/echart/EChartUtils'
import { getDate } from 'utils/DateUtil'
import { getBeginingOfTheYear, getYear } from 'utils/DateUtil'
import { grey, greyBlue, THRESHOLD_COLORS_CODES } from 'utils/constants/ColorTheme'
import { exportFile } from 'utils/ExportDataUtil'
import { convertToRGBA } from 'utils/ColorUtil'
import { DEFAULT_OBJECTIVE } from 'catchment/constants/CatchmentConstant'
import { QualityThresholdType } from 'quality/shapes/QualityThresholdShapes'
import DtoAnalysisLight from 'quality/dto/analyse/DtoAnalysisLight'
import { Button, Dialog, DialogActions, DialogContent } from '@mui/material'
import useBoolean from 'utils/customHook/useBoolean'
import { hasValue } from 'utils/NumberUtil'

const NO_ANALYSIS_PERCENT = -1

const CATCHMENT_EXPORT_HEADERS = ['year', 'code', 'catchment']
const POINTS_EXPORT_HEADERS = ['year', 'code', 'catchmentPoint', 'cityCode', 'cityLabel']

const labelFormatter = (nbYears) => {
    if (nbYears > 75) return 4
    if (nbYears > 50) return 3
    if (nbYears > 25) return 2
    return 1
}

const ObjectiveChart = ({
    analysis = [],
    objective = DEFAULT_OBJECTIVE,
    threshold,
    specificThresholds = {},
    catchmentAndQualito = [],
    fileName = '',
    title = '',
    startPeriod,
    endPeriod,
}) => {
    const {
        cities,
    } = useSelector(store => ({
        cities: store.CityReducer.cities,
    }), shallowEqual)

    const {
        value: featuresVisibility,
        setTrue: showFeatures,
        setFalse: hideFeatures,
    } = useBoolean(false)
    const {
        value: alternativeCalculMethod,
        toggle: toggleCalculMethod,
    } = useBoolean(false)
    const {
        value: isFullScreen,
        toggle: toggleFullScreen,
        setFalse: closeFullScreen,
    } = useBoolean(false)

    const calculMethodRef = useRef(alternativeCalculMethod)

    useEffect(() => {
        calculMethodRef.current = alternativeCalculMethod
    }, [alternativeCalculMethod])

    const dates = useMemo(() => range(getYear(startPeriod), getYear(endPeriod)+1), [startPeriod, endPeriod])

    const serie = useMemo(() => (
        groupBy(dates.flatMap(d => {
            const yearFiltered = analysis.filter(({ sampleDate, result }) => hasValue(sampleDate) && hasValue(result) && getYear(sampleDate) === d)
            const qualitoFiltered = yearFiltered.filter(a => catchmentAndQualito.qualito?.includes(a.qualitometer))

            if (!alternativeCalculMethod) {
                const totalAnalysisCatchment = qualitoFiltered.length
                const totalOkCatchment = qualitoFiltered.filter(a => a.result < (specificThresholds[a.parameter]?.threshold1 || threshold)).length
                const percentOkCatchment = totalAnalysisCatchment > 0 ? totalOkCatchment / totalAnalysisCatchment * 100 : NO_ANALYSIS_PERCENT
                return {
                    date: getBeginingOfTheYear(d),
                    name: i18n.indicator,
                    data: qualitoFiltered,
                    value: round(percentOkCatchment, 5),
                    symbol: 'circle',
                    symbolSize: 6,
                }
            }

            return catchmentAndQualito.points.flatMap(p => {
                const qualitometer = p.qualito?.[0] || null
                const city = qualitometer && cities.find(c => c.code === qualitometer.townCode)
                const pointFiltered = qualitoFiltered.filter(a => p.qualito.map(q => q.id).includes(a.qualitometer))
                const totalAnalysisPoint = pointFiltered.length
                const totalOkPoint = pointFiltered.filter(a => a.result < (specificThresholds[a.parameter]?.threshold1 || threshold)).length
                const percentOkPoint = totalAnalysisPoint > 0 ? totalOkPoint / totalAnalysisPoint * 100 : NO_ANALYSIS_PERCENT
                return {
                    date: getBeginingOfTheYear(d),
                    name: `${p.codeWithoutDesignation ? `[${p.codeWithoutDesignation}]` : ''} ${qualitometer?.name} ${city?.name ? `- ${city.name}` : ''}`,
                    code: p.codeWithoutDesignation,
                    qualitoName: qualitometer?.name,
                    cityName: city?.name,
                    cityCode: qualitometer?.townCode,
                    mainPoint: p.mainPoint,
                    data: pointFiltered,
                    value: round(percentOkPoint, 5),
                    symbol: percentOkPoint === NO_ANALYSIS_PERCENT ? 'none' : 'circle',
                    symbolSize: 6,
                }
            })
        }), 'name')
    ), [dates, catchmentAndQualito, analysis, threshold, specificThresholds, alternativeCalculMethod, cities])
    const serieRef = useRef(serie)

    useEffect(() => {
        serieRef.current = serie
    }, [serie])

    const pointsColor = useMemo(() => alternativeCalculMethod ? {} : { itemStyle: { color: 'black' } }, [alternativeCalculMethod])

    const series = useMemo(() => (
        [...entries(serie).map(s => {
            return s[0] !== 'undefined' ? {
                type: 'line',
                name: s[0],
                data: s[1],
                ... pointsColor,
                lineStyle: { opacity: 0 },
                animationDuration: 500,
            } : undefined
        }),
        {
            type: 'line',
            name: 'Objectif non atteint',
            data: [],
            itemStyle: { color: convertToRGBA(THRESHOLD_COLORS_CODES.RED, 0.2) },
            lineStyle: { opacity: 0 },
            showSymbol: false,
            isArea: true,
        },
        {
            type: 'line',
            name: 'Objectif atteint',
            data: [],
            itemStyle: { color: convertToRGBA(THRESHOLD_COLORS_CODES.GREEN, 0.2) },
            lineStyle: { opacity: 0 },
            showSymbol: false,
            isArea: true,
            markArea: {
                data: [
                    [
                        { yAxis: 0, itemStyle: {
                            color: THRESHOLD_COLORS_CODES.RED,
                            opacity: 0.2,
                        } },
                        { yAxis: objective },
                    ],
                    [
                        { yAxis: objective, itemStyle: {
                            color: THRESHOLD_COLORS_CODES.GREEN,
                            opacity: 0.2,
                        } },
                        { yAxis: 100 },
                    ],
                ],
            },
        },
        ]), [objective, serie, pointsColor])

    const labelInterval = useMemo(() => labelFormatter(dates.length), [dates.length])

    const options = {
        title: {
            text: title,
            left: 'center',
        },
        series,
        xAxis: [{
            type: 'category',
            data: dates,
            boundaryGap: true,
            showSplitLine: true,
            axisLabel: {
                rotate: 50,
                interval: 0,
                formatter: (value, index) => index % labelInterval === 0 ? value : undefined,
            },
        }],
        yAxis: [{
            type: 'value',
            position: 'left',
            boundaryGap: true,
            min: 0,
            max: 100,
            name: alternativeCalculMethod ? `${i18n.bySamplePointConformity} (%)` : `${i18n.catchmentConformity} (%)`,
            nameLocation: 'center',
            nameGap: 45,
        }],
        legend: {
            top: '55',
            left: '10%',
            right: featuresVisibility ? 150 : '8%',
            type: 'scroll',
            data: series.map(s => ({ name: s?.name, icon: s?.isArea ? 'roundRect' : null })),
        },
        grid: {
            top: '100',
            left: '6%',
            right: '3%',
            bottom: '10',
            containLabel: true,
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow',
                snap: true,
            },
            formatter: params => {
                const hasData = params.length > 0
                if (!hasData) return undefined
                const areEmpty = params.filter(p => p.data?.data?.length > 0).length === 0
                if (areEmpty) return undefined

                const yearData = params[0].data ?? { data: {} }
                const date = yearData.date ? getDate(yearData.date, 'YYYY') : i18n.noYear

                if (params.length === 1 && params[0].seriesName === i18n.indicator) {
                    return yearData.symbol === 'none' ? undefined : `${i18n.year} ${date}<br>${catchmentAndQualito.code ? `[${catchmentAndQualito.code}]` : ''} ${catchmentAndQualito.name} : ${yearData.value}%`
                }

                params.sort((a, b) => {
                    if (a?.data?.value === undefined) return 1
                    if (b?.data?.value === undefined) return -1
                    return b?.data?.value - a?.data?.value
                })

                return params.reduce((acc, act) => act.data?.symbol === 'none' ? acc : `${acc}<br>${act.data?.name} : ${act.data?.value}%`, `${i18n.year} ${date}`)
            },
        },
        toolbox: {
            top: '50',
            right: '35',
            showTitle: false,
            itemSize: '18',
            tooltip: {
                show: true,
                position: 'bottom',
            },
            feature: {
                myToolNamed: {
                    show: featuresVisibility,
                    title: i18n.changeCalcMethod,
                    icon: operationIcon,
                    onclick: () => toggleCalculMethod(),
                    iconStyle: {
                        borderColor: alternativeCalculMethod ? greyBlue : grey,
                    },
                },
                myToolFullScreen: {
                    show: featuresVisibility,
                    title: i18n.fullScreen,
                    icon: isFullScreen ? fullScreenExitIcon : fullScreenIcon,
                    onclick: () => toggleFullScreen(),
                },
                saveAsImage: {
                    show: featuresVisibility,
                    title: i18n.pictureExport,
                    icon: exportPictureIcon,
                    name: fileName,
                },
                myToolExport: {
                    show: featuresVisibility,
                    title: i18n.excelExport,
                    icon: exportExcelIcon,
                    onclick: () => {
                        const exportData = compact(entries(serieRef.current).flatMap(s => {
                            const [key, val] = s
                            if (key === 'undefined') return undefined
                            return val.flatMap(({ date, data, code, cityName, cityCode, qualitoName }) => {
                                const totalAnalysis = data?.length
                                const percentOk = totalAnalysis > 0 ? data.filter(a => a.result < threshold).length / totalAnalysis * 100 : 0
                                const percentKo = totalAnalysis > 0 ? 100 - percentOk : 0

                                const commonReturn = {
                                    conformity: { value: percentOk >= objective ? i18n.yes : i18n.no },
                                    totalAnalysis: { value: totalAnalysis, format: '0', cellType: 'number' },
                                    percentOk: { value: `${round(percentOk, 2)}%`, cellType: 'right' },
                                    percentKo: { value: `${round(percentKo, 2)}%`, cellType: 'right' },
                                }

                                return !calculMethodRef.current ?
                                    {
                                        year: { value: getDate(date, 'YYYY'), cellType: 'right' },
                                        sortYear: getDate(date, 'YYYY'),
                                        code: { value: catchmentAndQualito.code, cellType: 'left' },
                                        catchment: catchmentAndQualito.name,
                                        ... commonReturn,
                                    } :
                                    {
                                        year: { value: getDate(date, 'YYYY'), cellType: 'right' },
                                        sortYear: getDate(date, 'YYYY'),
                                        code: { value: code, cellType: 'left' },
                                        catchmentPoint: qualitoName,
                                        cityCode,
                                        cityLabel: cityName,
                                        ... commonReturn,
                                    }
                            })
                        }))

                        const sortedData = orderBy(exportData, ['sortYear'], ['asc'])
                        const specificColumns = calculMethodRef.current ? POINTS_EXPORT_HEADERS : CATCHMENT_EXPORT_HEADERS
                        exportFile({
                            data: exportData.length ? [
                                {
                                    ...sortedData[0],
                                    headers: [...specificColumns, 'conformity', 'totalAnalysis', 'percentOk', 'percentKo'],
                                    customHeaders: [...specificColumns, 'conformity', 'nbQuantifications', `${i18n.quantificationBelow} ${keys(specificThresholds).length > 0 ? i18n.specificThresholdToBeUsed : threshold}`, `${i18n.quantificationHigher}= ${keys(specificThresholds).length > 0 ? i18n.specificThresholdToBeUsed : threshold}`],
                                },
                                ...sortedData.slice(1),
                            ] : [],
                            exportType: 'xlsx',
                            titleFile: fileName,
                        }, true)
                    },
                },
            },
        },
    }

    return (
        <>
            <div
                onMouseOver={() => showFeatures()}
                onMouseOut={() => hideFeatures()}
            >
                <ReactECharts
                    echarts={echarts}
                    option={options}
                    notMerge
                    lazyUpdate
                    style={{ height: 400 }}
                />
            </div>
            <Dialog
                fullScreen
                open={isFullScreen}
            >
                <DialogContent>
                    <div
                        onMouseOver={() => showFeatures()}
                        onMouseOut={() => hideFeatures()}
                    >
                        <ReactECharts
                            echarts={echarts}
                            option={options}
                            notMerge
                            lazyUpdate
                            style={{ height: '75vh' }}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeFullScreen} variant='contained'>
                        {i18n.close}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

ObjectiveChart.propTypes = {
    analysis: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.instanceOf(DtoAnalysisLight),
        PropTypes.shape({
            result: PropTypes.number,
            sampleDate: PropTypes.number,
            qualitometer: PropTypes.number,
            parameter: PropTypes.string,
        }),
    ])),
    objective: PropTypes.number,
    threshold: PropTypes.number,
    specificThresholds: PropTypes.objectOf(QualityThresholdType),
    catchmentAndQualito: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        code: PropTypes.string,
        points: PropTypes.arrayOf(PropTypes.shape({
            codeWithoutDesignation: PropTypes.string,
            mainPoint: PropTypes.bool,
            qualito: PropTypes.arrayOf(PropTypes.shape({
                id: PropTypes.number,
                townCode: PropTypes.string,
                name: PropTypes.string })),
        })),
        qualito: PropTypes.arrayOf(PropTypes.number),
    }),
    areaColor: PropTypes.arrayOf(PropTypes.string),
    fileName: PropTypes.string,
    title: PropTypes.string,
    startPeriod: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
    endPeriod: PropTypes.number,
}

export default ObjectiveChart